import { cqrs, setPathParameters } from '@faro/http-clients';
import type { Identified, Revisioned } from '@faro/realtime-services';
import {
    EntityCommandCreateResponse,
    RevisionedCommandResponse,
    Study,
    StudyStatus,
    StudyWithChildren,
    toStudy,
} from '@faro/study-designer-model';
import axios, { AxiosResponse } from 'axios';
import { RevisionQueryParameter } from '../parameter';

export type StudyQueryParameters = {
    readonly includeDeleted?: boolean;
};

export interface StudyStatusUpdate extends StudyStatus {
    id: string;
}

export interface SearchStudyPaginatedRequest {
    defaultPage?: number;
    defaultSize?: number;
    maxSize?: number;
    searchPattern?: string;
    includeSubStudies?: boolean;
    studyId?: string;
    studySpaceId?: string;
}

export interface BaseEntityQuery {
    readonly ids?: string[];
    readonly includeDeleted?: boolean;
}

export interface StudyEntityQuery extends BaseEntityQuery, RevisionQueryParameter {
    readonly studyId?: string;
    readonly parentStudyIds?: string[];
}

export interface StudyFindAllQuery extends StudyEntityQuery {
    searchPattern?: string;
    ids?: string[];
    studySpaceId?: string;
    defaultStudyId?: string;
    includeSubStudies?: boolean;
}

export interface StudyGetQuery extends RevisionQueryParameter {
    readonly id: string;
}

export const studyClient = {
    ...cqrs<Study>('/api/study-designer/studies', toStudy).writableEntityClient<StudyQueryParameters>(),

    get: (query: StudyGetQuery, signal?: AbortSignal): Promise<Study> => {
        const { id, revision } = query;
        const path = `/api/study-designer/studies/${id}`;
        return axios
            .get<Study>(path, { params: revision ? { revision } : undefined, signal })
            .then(response => toStudy(response.data));
    },

    list: (query?: StudyFindAllQuery, signal?: AbortSignal): Promise<Study[]> => {
        const path = '/api/study-designer/studies';
        return axios.get<Study[]>(path, { params: query, signal }).then(response => response.data);
    },

    clone: cqrs<Study, Identified>('/api/study-designer/studies/:id/actions/clone').create(),

    updateStatus: (entity: Partial<Identified> & StudyStatusUpdate): Promise<Identified & Revisioned> => {
        const path = setPathParameters('/api/study-designer/studies/:id/actions/update-status', entity as any);
        const { id, ...body } = entity as Identified & StudyStatusUpdate;
        return axios
            .post<
                Omit<Partial<Identified> & StudyStatusUpdate, 'id'>,
                AxiosResponse<RevisionedCommandResponse<EntityCommandCreateResponse>>
            >(path, body)
            .then(response => ({
                id: response.data.entityId,
                revision: response.data.revision,
            }));
    },

    searchTree: (query?: SearchStudyPaginatedRequest, signal?: AbortSignal): Promise<StudyWithChildren[]> => {
        const path = '/api/study-designer/studies/actions/search/tree';
        return axios.get<StudyWithChildren[]>(path, { params: query, signal }).then(response => response.data);
    },

    getSourceConversationId: (studyId: string, signal?: AbortSignal): Promise<string | null> => {
        const path = `/api/study-designer/studies/${studyId}/actions/source-conversation-id`;
        return axios
            .get<{ conversationId: string | null }>(path, { signal })
            .then(response => response.data.conversationId);
    },
};
